import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";
import { selectCurrentUserUUID } from "Reducers/app/selectors";

import {
  BillingProfileStanding,
  getStandingRequest,
  getUserStandingsRequest
} from "Api/billingSystem";

import { selectUserBillingProfileDetails } from "./profiles";

import type { RootState } from "Store/configureStore";

// A freshly created billing profile transiently returns 403 from `/standing`
// while it finishes provisioning (CON-1334). Detect that status so it can be
// treated as a clean standing instead of a user-facing Forbidden error.
const isForbiddenError = (err: unknown): boolean => {
  const { code, status } =
    (err as { code?: number; status?: number } | null) ?? {};
  return code === 403 || status === 403;
};

// Thunks
export const getStandingThunk = createAppAsyncThunk(
  "billingSystem/standing/getByProfile",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      return await getStandingRequest(billingProfileId);
    } catch (err) {
      // Right after profile creation `/standing` can transiently 403. Treat it
      // as a clean standing rather than surfacing a Forbidden error; a later
      // load returns the real standing (CON-1334).
      if (isForbiddenError(err)) {
        const cleanStanding: BillingProfileStanding = {
          billing_profile_id: billingProfileId,
          has_disputes: false,
          has_past_dues: false,
          manual_dunning: false
        };
        return cleanStanding;
      }

      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  },
  {
    condition: ({ billingProfileId }, { getState }) => {
      const entry = getState().profileStanding.byProfileId[billingProfileId];

      // Don't fetch if already loading, loaded or errored
      if (entry?.loading || entry?.data || entry?.error) {
        return false;
      }

      return true;
    }
  }
);

// Standing for every profile the user can see, in one call. Populates the
// same per-profile map as getStandingThunk, so account-level indicators (the
// nav dot) work on page load instead of waiting for the user menu to mount
// its per-profile fetches.
export const getUserStandingsThunk = createAppAsyncThunk(
  "billingSystem/standing/getByUser",
  async (_, { getState, rejectWithValue }) => {
    try {
      const userId = selectCurrentUserUUID(getState());
      return await getUserStandingsRequest(userId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  },
  {
    condition: (_, { getState }) => {
      const { userLoading, userLoaded, userError } = getState().profileStanding;

      // Don't fetch if already loading, loaded or errored
      return !userLoading && !userLoaded && !userError;
    }
  }
);

// State type
type StandingState = {
  byProfileId: Record<
    string,
    {
      loading: boolean;
      data?: BillingProfileStanding;
      error?: string;
    }
  >;
  userLoading: boolean;
  userLoaded: boolean;
  userError?: string;
};

const initialState: StandingState = {
  byProfileId: {},
  userLoading: false,
  userLoaded: false
};

// Slice
const standingSlice = createSlice({
  name: "standing",
  initialState,
  reducers: {},
  extraReducers: builder => {
    // Get standing by profile
    builder
      .addCase(getStandingThunk.pending, (state, { meta }) => {
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "loading"],
          true
        );
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "error"],
          undefined
        );
      })
      .addCase(getStandingThunk.fulfilled, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "data"],
          action.payload
        );
      })
      .addCase(getStandingThunk.rejected, (state, action) => {
        const { billingProfileId } = action.meta.arg;

        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "error"],
          action.payload?.error
        );
      });

    // Get standings for all the user's profiles
    builder
      .addCase(getUserStandingsThunk.pending, state => {
        state.userLoading = true;
        state.userError = undefined;
      })
      .addCase(getUserStandingsThunk.fulfilled, (state, action) => {
        state.userLoading = false;
        state.userLoaded = true;
        action.payload.items.forEach(standing => {
          state.byProfileId[standing.billing_profile_id] = {
            loading: false,
            data: standing
          };
        });
      })
      .addCase(getUserStandingsThunk.rejected, (state, action) => {
        state.userLoading = false;
        state.userError = action.payload?.error;
      });
  }
});

export default standingSlice.reducer;

// Selectors
const standingStateSelector = (state: RootState) => state.profileStanding;

export const selectProfileStanding = createSelector(
  standingStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => {
    return state.byProfileId[billingProfileId]?.data;
  }
);

export const selectProfileStandingLoading = createSelector(
  standingStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.byProfileId[billingProfileId]?.loading ?? false
);

export const selectProfileStandingError = createSelector(
  standingStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.byProfileId[billingProfileId]?.error
);

export const selectUserStandingsLoaded = createSelector(
  standingStateSelector,
  state => state.userLoaded
);

export const selectUserStandingsLoading = createSelector(
  standingStateSelector,
  state => state.userLoading
);

export const selectUserStandingsError = createSelector(
  standingStateSelector,
  state => state.userError
);

// Severity rank per billing account, derived from its profiles' standings:
// 0 — some profile has pending payments (past dues), 1 — some profile has a
// dispute, 2 — clean. Account lists use it to float the accounts that need
// attention to the top; accounts without loaded standings simply get no rank.
export const selectBillingAccountStandingRanks = createSelector(
  selectUserBillingProfileDetails,
  standingStateSelector,
  (details, standing): Record<string, number> => {
    const ranks: Record<string, number> = {};

    details.forEach(detail => {
      const data = standing.byProfileId[detail.billing_profile_id]?.data;
      if (!data) return;

      const rank = data.has_past_dues ? 0 : data.has_disputes ? 1 : 2;
      const current = ranks[detail.billing_account_id];
      if (current === undefined || rank < current) {
        ranks[detail.billing_account_id] = rank;
      }
    });

    return ranks;
  }
);

export const selectAnyProfileHasDisputes = createSelector(
  standingStateSelector,
  state =>
    Object.values(state.byProfileId).some(entry => entry.data?.has_disputes)
);
export const selectAnyProfileHasPastDues = createSelector(
  standingStateSelector,
  state =>
    Object.values(state.byProfileId).some(entry => entry.data?.has_past_dues)
);
